import { getHeaderOffset } from './offsetCalculators';

// Show vehicle toolbar with analytics tracking
export const showVehicleToolbar = ({ dom, config, DDC, isPageLoad = false }) => {
	const offset = getHeaderOffset({ dom, config, DDC, isPageLoad });
	const { vehicleToolbar } = dom;
	vehicleToolbar.style.top = `${offset}px`;
	vehicleToolbar.classList.remove('d-none');
	DDC.fireTrackingEvent({
		widgetName: 'ws-vehicle-toolbar',
		widgetId: 'vehicle-toolbar1',
		event: 'widgetUserEvent',
		element: 'Sticky Header',
		elementCTA: undefined,
		action: 'view',
		result: 'Sticky header impression',
		nonInteractive: false,
		widgetState: 'default',
		value: undefined
	});
};

// Check if target element is above the visible viewport area
export const isTargetAboveVisibleArea = (
	{ isIntersecting, boundingClientRect, rootBounds, target },
	isPageLoad = false
) => {
	const targetBounds = isPageLoad
		? target.getBoundingClientRect()
		: boundingClientRect;
	return !isIntersecting && targetBounds.bottom < rootBounds.top;
};

// Create toolbar visibility handler
export const createSetVehicleToolbarVisibility = ({ dom, config, DDC }) => {
	return (entries) => {
		if (document.readyState === 'complete') {
			if (isTargetAboveVisibleArea(entries[0])) {
				showVehicleToolbar({ dom, config, DDC });
			} else {
				dom.vehicleToolbar.classList.add('d-none');
			}
		} else {
			window.addEventListener(
				'load',
				() => {
					setTimeout(() => {
						if (isTargetAboveVisibleArea(entries[0], true)) {
							showVehicleToolbar({dom,
								config,
								DDC,
								isPageLoad: true
							});
						}
					}, 50);
				},
				{ once: true }
			);
		}
	};
};

// Create vehicle CTAs intersection observer
export const createVehicleCtasObserver = ({
	dom,
	config,
	DDC,
	setVehicleToolbarVisibility
}) => {
	let vehicleCtasObserver;

	const setup = () => {
		if (vehicleCtasObserver) {
			vehicleCtasObserver.disconnect();
		}

		const offset = getHeaderOffset({ dom, config, DDC });
		const options = {
			rootMargin: `-${offset}px 0px 0px`
		};

		vehicleCtasObserver = new window.IntersectionObserver(
			setVehicleToolbarVisibility,
			options
		);
		vehicleCtasObserver.observe(dom.vehicleCtas);
	};

	const cleanup = () => {
		if (vehicleCtasObserver) vehicleCtasObserver.disconnect();
	};

	return { setup, cleanup };
};

/**
 * Create resize handler for desktop
 * Debounces resize events to avoid excessive recalculations
*/
export const createResizeHandler = ({ DDC, setupVehicleCtasObserver }) => {
	if (DDC.client.isMobile) {
		return { setup: () => {}, cleanup: () => {} };
	}

	let resizeTimeout;

	const resizeHandler = () => {
		if (resizeTimeout) clearTimeout(resizeTimeout);

		resizeTimeout = setTimeout(() => {
			setupVehicleCtasObserver();
		}, 800); // timeout for iOS Safari
	};

	const setup = () => {
		window.addEventListener('resize', resizeHandler);
	};

	const cleanup = () => {
		window.removeEventListener('resize', resizeHandler);
		if (resizeTimeout) clearTimeout(resizeTimeout);
	};

	return { setup, cleanup };
};

/**
 * Create alert banner observer for mobile
 * Watches for alert banner visibility changes to recalculate toolbar position
*/
export const createAlertBannerObserver = ({
	dom,
	config,
	setupVehicleCtasObserver
}) => {
	const alertBanner = dom.header.querySelector('.content-alert-banner');

	if (!alertBanner || !config.isEntireMobileHeaderFixed) {
		return { setup: () => {}, cleanup: () => {} };
	}

	let alertObserver;

	const setup = () => {
		alertObserver = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (
					!entry.isIntersecting ||
					entry.target.classList.contains('hide') ||
					getComputedStyle(entry.target).display === 'none'
				) {
					requestAnimationFrame(setupVehicleCtasObserver);
				}
			});
		});
		alertObserver.observe(alertBanner);
	};

	const cleanup = () => {
		if (alertObserver) alertObserver.disconnect();
	};

	return { setup, cleanup };
};
